import React, { useState, useRef, useEffect } from 'react';
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiUsers,
  FiMessageSquare,
  FiCheckCircle,
  FiLock,
  FiAlertCircle,
  FiChevronRight,
  FiPlus,
  FiMinus,
  FiStar,
  FiLoader,
  FiInfo,
  FiTag,
  FiCreditCard,
} from 'react-icons/fi';
import './HoneymoonDetailsPage.css';
import api from '../../../Api';
import { useNavigate } from 'react-router-dom';

export default function HoneymoonBookingSection({ packageData, destinationName, plans }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    guests: 2,
    wantVeg: true,
    wantNonVeg: true,
    specialNote: '',
    couponCode: '',
  });
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanDetail, setSelectedPlanDetail] = useState(null);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [addons, setAddons] = useState([]);
  const [pricePreview, setPricePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);
  const navigate = useNavigate();

  // Payment states
  const [paymentStep, setPaymentStep] = useState('idle');
  const [orderData, setOrderData] = useState(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // --- Fetch plan details when a plan is selected ---
  useEffect(() => {
    if (!selectedPlanId) {
      setSelectedPlanDetail(null);
      return;
    }
    const fetchPlanDetail = async () => {
      setPlanDetailLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await api.get(`/honeymoon/plans/${selectedPlanId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.status) {
          setSelectedPlanDetail(response.data.data);
          setAddons([]);
        } else {
          setError(response.data.message || 'Failed to load plan details');
        }
      } catch (err) {
        console.error('Plan detail fetch error:', err);
        setError('Could not load plan details. Please try again.');
      } finally {
        setPlanDetailLoading(false);
      }
    };
    fetchPlanDetail();
  }, [selectedPlanId]);

  // Restore booking state after login redirect
  useEffect(() => {
    const saved = sessionStorage.getItem('honeymoonBookingRedirect');
    if (saved) {
      try {
        const savedState = JSON.parse(saved);
        if (savedState.shouldOpenModal && savedState.packageId === packageData?.id) {
          setFormData(savedState.formData || formData);
          setSelectedPlanId(savedState.selectedPlanId || null);
          setAddons(savedState.addons || []);
          setIsModalOpen(true);
        }
        sessionStorage.removeItem('honeymoonBookingRedirect');
      } catch (e) {
        console.error('Failed to restore booking state', e);
        sessionStorage.removeItem('honeymoonBookingRedirect');
      }
    }
  }, [packageData]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isModalOpen]);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsModalOpen(false);
      }
    };
    if (isModalOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalOpen]);

  // Load Razorpay script
  useEffect(() => {
    if (window.Razorpay) {
      setRazorpayLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // --- Helpers ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const getAvailableAddons = () => {
    if (!selectedPlanDetail || !selectedPlanDetail.itinerary) return [];
    const allAddons = [];
    selectedPlanDetail.itinerary.days.forEach(day => {
      if (day.addons) {
        day.addons.forEach(a => {
          if (!allAddons.find(ex => ex.id === a.id)) {
            allAddons.push({ ...a, quantity: 0 });
          }
        });
      }
    });
    return allAddons;
  };

  const handleAddonQuantity = (addonId, change) => {
    setAddons(prev => {
      const existing = prev.find(a => a.id === addonId);
      if (existing) {
        const newQty = Math.max(0, existing.quantity + change);
        if (newQty === 0) return prev.filter(a => a.id !== addonId);
        return prev.map(a => a.id === addonId ? { ...a, quantity: newQty } : a);
      } else {
        if (change > 0) return [...prev, { id: addonId, quantity: 1 }];
        return prev;
      }
    });
  };

  // Fetch price preview
  useEffect(() => {
    if (!selectedPlanId) return;
    const fetchPreview = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await api.post(
          '/honeymoon/pricing/preview',
          {
            plan_id: selectedPlanId,
            addons: addons.map(a => ({ id: a.id, quantity: a.quantity })),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status) {
          setPricePreview(response.data.data);
        } else {
          setError(response.data.message || 'Failed to fetch pricing');
        }
      } catch (err) {
        console.error('Price preview error:', err);
        setError('Could not fetch price preview.');
      }
    };
    fetchPreview();
  }, [selectedPlanId, addons]);

  // --- Navigation handlers ---
  const handleNext = () => {
    if (step === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        setError('Please fill in all required contact fields.');
        return;
      }
      if (!formData.date) {
        setError('Please select a tour start date.');
        return;
      }
      setError(null);
    }
    if (step === 2) {
      if (!selectedPlanId) {
        setError('Please select a plan.');
        return;
      }
      setError(null);
    }
    if (step < 3) setStep(step + 1);
  };

  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
    setError(null);
  };

  // --- Book Now (auth check) ---
  const handleBookNow = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      const bookingState = {
        packageId: packageData?.id,
        shouldOpenModal: true,
        formData,
        selectedPlanId,
        addons,
      };
      sessionStorage.setItem('honeymoonBookingRedirect', JSON.stringify(bookingState));
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/signup?redirect=${encodeURIComponent(currentPath)}`;
      return;
    }
    setIsModalOpen(true);
  };

  // --- Submit booking (with coupon support) ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setPaymentStep('idle');

    const payload = {
      plan_id: selectedPlanId,
      tour_start_date: formData.date,
      contact_name: formData.name,
      contact_mobile: formData.phone,
      contact_email: formData.email,
      want_veg_food: formData.wantVeg,
      want_nonveg_food: formData.wantNonVeg,
      addons: addons.map(a => ({ id: a.id, quantity: a.quantity })),
      special_note: formData.specialNote || '',
      geo: {
        lat: 22.5726,
        lng: 88.3639,
        accuracy_meters: 12.5,
        source: 'gps',
      },
    };

    if (formData.couponCode && formData.couponCode.trim() !== '') {
      payload.coupon_code = formData.couponCode.trim();
    }

    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.post('/honeymoon/bookings', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status) {
        setBookingResult(response.data.data);
        setPaymentStep('idle');
      } else {
        throw new Error(response.data.message || 'Booking failed.');
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        const bookingState = {
          packageId: packageData?.id,
          shouldOpenModal: true,
          formData,
          selectedPlanId,
          addons,
        };
        sessionStorage.setItem('honeymoonBookingRedirect', JSON.stringify(bookingState));
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      } else {
        setError(err.message || 'An error occurred. Please try again.');
        setPaymentStep('failed');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Create Razorpay Order & Open Checkout ---
  const handlePayNow = async () => {
    if (!bookingResult || !bookingResult.booking_id) {
      setError('Booking information missing.');
      return;
    }
    setPaymentStep('creating');
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const amount = bookingResult.grand_total || 0;
      const response = await api.post(
        `/honeymoon/bookings/${bookingResult.booking_id}/razorpay-order`,
        {
          amount: amount,
          payment_type: 'advance',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status) {
        const order = response.data.data;
        setOrderData(order);
        openRazorpayCheckout(order, bookingResult.booking_id);
      } else {
        throw new Error(response.data.message || 'Failed to create payment order.');
      }
    } catch (err) {
      setError(err.message || 'Payment order creation failed.');
      setPaymentStep('failed');
    }
  };

  // --- Verify payment after Razorpay success ---
  const verifyPayment = async (bookingId, paymentId, orderId, signature) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.post(
        `/honeymoon/bookings/${bookingId}/razorpay-verify`,
        {
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Payment verification failed.');
    }
  };

  const openRazorpayCheckout = (order, bookingId) => {
    if (!razorpayLoaded) {
      setError('Razorpay SDK not loaded. Please refresh and try again.');
      setPaymentStep('failed');
      return;
    }

    const options = {
      key: order.key_id,
      amount: order.amount_paise,
      currency: order.currency,
      name: packageData?.name || 'Honeymoon Package',
      description: `Booking ${order.receipt}`,
      order_id: order.order_id,
      prefill: {
        name: order.prefill?.name || formData.name,
        email: order.prefill?.email || formData.email,
        contact: order.prefill?.contact || formData.phone,
      },
      theme: { color: '#c9a227' },
      modal: {
        ondismiss: () => {
          setPaymentStep('idle');
          setError('Payment was cancelled.');
        },
      },
      handler: async (response) => {
        setPaymentStep('processing');
        try {
          // Call verify API
          const verifyResult = await verifyPayment(
            bookingId,
            response.razorpay_payment_id,
            response.razorpay_order_id,
            response.razorpay_signature
          );
          if (verifyResult.status) {
            // Payment successful - update booking result if needed
            setBookingResult(prev => ({
              ...prev,
              payment_status: 'paid',
              verified_data: verifyResult.data,
            }));
            setPaymentStep('completed');
          } else {
            throw new Error(verifyResult.message || 'Payment verification failed.');
          }
        } catch (err) {
          setPaymentStep('failed');
          setError(err.message);
        }
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
    setPaymentStep('processing');
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      guests: 2,
      wantVeg: true,
      wantNonVeg: true,
      specialNote: '',
      couponCode: '',
    });
    setSelectedPlanId(null);
    setSelectedPlanDetail(null);
    setAddons([]);
    setPricePreview(null);
    setBookingResult(null);
    setError(null);
    setPaymentStep('idle');
    setOrderData(null);
    sessionStorage.removeItem('honeymoonBookingRedirect');
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);

  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const availableAddons = getAvailableAddons();

  // Plan selection UI
  const renderPlanOptions = () => {
    return (
      <div className="pkgd-plan-selector-grid">
        {plans.map((plan) => {
          const price = plan.couple_price || plan.price_per_person * (plan.pax_count || 2);
          const isSelected = selectedPlanId === plan.id;
          const isLoading = planDetailLoading && isSelected;
          return (
            <div
              key={plan.id}
              className={`pkgd-plan-selector-card ${isSelected ? 'pkgd-selected' : ''}`}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              <div className="pkgd-plan-selector-header">
                <span className="pkgd-plan-tier-badge">{plan.tier_label || plan.tier}</span>
                <span className="pkgd-plan-selector-price">{formatPrice(price)}</span>
              </div>
              <h4 className="pkgd-plan-selector-name">{plan.plan_name}</h4>
              <p className="pkgd-plan-selector-pax">{plan.pax_count} guests</p>
              <div className="pkgd-plan-selector-badges">
                {plan.has_veg_food && <span className="pkgd-tag pkgd-tag-veg">Veg</span>}
                {plan.has_nonveg_food && <span className="pkgd-tag pkgd-tag-nonveg">Non‑Veg</span>}
              </div>
              {isLoading && (
                <div className="pkgd-plan-selector-loading">
                  <FiLoader className="pkgd-spinner" />
                </div>
              )}
              {isSelected && !isLoading && (
                <div className="pkgd-plan-selector-check">
                  <FiCheckCircle />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Render payment status overlay inside modal
  const renderPaymentStatus = () => {
    if (paymentStep === 'creating' || paymentStep === 'processing') {
      return (
        <div className="pkgd-payment-loading">
          <FiLoader className="pkgd-spinner" />
          <p>{paymentStep === 'creating' ? 'Preparing payment...' : 'Processing your payment...'}</p>
        </div>
      );
    }
    if (paymentStep === 'completed') {
      return (
        <div className="pkgd-payment-success">
          <FiCheckCircle className="pkgd-success-icon" />
          <h3>Payment Successful!</h3>
          <p>Your honeymoon booking is now confirmed.</p>
          {orderData && <p className="pkgd-booking-number">Order ID: {orderData.order_id}</p>}
        </div>
      );
    }
    if (paymentStep === 'failed') {
      return (
        <div className="pkgd-payment-failed">
          <FiAlertCircle className="pkgd-error-icon" />
          <h3>Payment Failed</h3>
          <p>{error || 'Something went wrong. Please try again.'}</p>
          <button className="pkgd-btn-retry" onClick={() => { setPaymentStep('idle'); setError(null); }}>
            Retry Payment
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Sidebar */}
      <div className="pkgd-booking-sidebar">
        <div className="pkgd-booking-card">
          <h3>Book This Honeymoon</h3>
          <div className="pkgd-booking-quick-info">
            <div className="pkgd-booking-info-item">
              <FiCalendar />
              <span>{packageData?.duration_days} Days</span>
            </div>
            <div className="pkgd-booking-info-item">
              <FiClock />
              <span>{packageData?.duration_nights} Nights</span>
            </div>
            <div className="pkgd-booking-info-item">
              <FiMapPin />
              <span>{destinationName}</span>
            </div>
          </div>
          <button className="pkgd-btn-book" onClick={handleBookNow}>
            Book Now <FiChevronRight />
          </button>
          <p className="pkgd-booking-note">
            <FiInfo />
            Free cancellation within 24 hours
          </p>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="pkgd-modal-overlay">
          <div className="pkgd-modal-container" ref={modalRef}>
            <button
              className="pkgd-modal-close"
              onClick={() => { setIsModalOpen(false); resetForm(); }}
            >
              <FiX />
            </button>

            <div className="pkgd-modal-header">
              <h2>Book Your Honeymoon</h2>
              <p>
                <strong>{packageData?.name}</strong> in {destinationName}
              </p>
            </div>

            {/* Payment status overlay */}
            {paymentStep !== 'idle' && paymentStep !== 'completed' && paymentStep !== 'failed' && (
              <div className="pkgd-payment-overlay">{renderPaymentStatus()}</div>
            )}

            {!bookingResult ? (
              <>
                <div className="pkgd-modal-steps">
                  <div className={`pkgd-step-indicator ${step >= 1 ? 'pkgd-active' : ''}`}>
                    <span>1</span>
                    <label>Contact</label>
                  </div>
                  <div className={`pkgd-step-indicator ${step >= 2 ? 'pkgd-active' : ''}`}>
                    <span>2</span>
                    <label>Select Plan</label>
                  </div>
                  <div className={`pkgd-step-indicator ${step >= 3 ? 'pkgd-active' : ''}`}>
                    <span>3</span>
                    <label>Review</label>
                  </div>
                </div>

                {error && (
                  <div className="pkgd-error-message">
                    <FiAlertCircle />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="pkgd-modal-form">
                  {/* Step 1: Contact Details */}
                  {step === 1 && (
                    <div className="pkgd-form-step">
                      <div className="pkgd-form-group">
                        <label><FiUser /> Full Name <span>*</span></label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiMail /> Email <span>*</span></label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiPhone /> Phone <span>*</span></label>
                        <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiCalendar /> Tour Start Date <span>*</span></label>
                        <input type="date" name="date" value={formData.date} onChange={handleChange} required />
                      </div>
                      <div className="pkgd-form-group">
                        <label><FiUsers /> Number of Guests</label>
                        <input type="number" name="guests" value={formData.guests} onChange={handleChange} min="2" max="20" />
                      </div>
                    </div>
                  )}

                  {/* Step 2: Plan Selection, Add-ons, Coupon */}
                  {step === 2 && (
                    <div className="pkgd-form-step">
                      <div className="pkgd-form-group">
                        <label><FiStar /> Select Plan <span>*</span></label>
                        {renderPlanOptions()}
                      </div>

                      {selectedPlanId && selectedPlanDetail && (
                        <>
                          <div className="pkgd-form-group">
                            <label>Food Preferences</label>
                            <div className="pkgd-checkbox-group">
                              <label>
                                <input
                                  type="checkbox"
                                  name="wantVeg"
                                  checked={formData.wantVeg}
                                  onChange={handleChange}
                                />
                                Veg
                              </label>
                              <label>
                                <input
                                  type="checkbox"
                                  name="wantNonVeg"
                                  checked={formData.wantNonVeg}
                                  onChange={handleChange}
                                />
                                Non‑Veg
                              </label>
                            </div>
                          </div>

                          {availableAddons.length > 0 && (
                            <div className="pkgd-form-group">
                              <label>Add‑ons</label>
                              {availableAddons.map((addon) => {
                                const selected = addons.find(a => a.id === addon.id);
                                const qty = selected ? selected.quantity : 0;
                                return (
                                  <div key={addon.id} className="pkgd-addon-item">
                                    <span>{addon.addon_name} (₹{addon.price_per_person}/person)</span>
                                    <div className="pkgd-quantity-control">
                                      <button
                                        type="button"
                                        onClick={() => handleAddonQuantity(addon.id, -1)}
                                      >
                                        <FiMinus />
                                      </button>
                                      <span>{qty}</span>
                                      <button
                                        type="button"
                                        onClick={() => handleAddonQuantity(addon.id, 1)}
                                      >
                                        <FiPlus />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="pkgd-form-group">
                            <label><FiTag /> Coupon Code (optional)</label>
                            <div className="pkgd-coupon-input">
                              <input
                                type="text"
                                name="couponCode"
                                value={formData.couponCode}
                                onChange={handleChange}
                                placeholder="Enter coupon code"
                              />
                            </div>
                          </div>

                          {pricePreview && (
                            <div className="pkgd-price-preview">
                              <div className="pkgd-price-row">
                                <span>Couple Price</span>
                                <span>{formatPrice(pricePreview.couple_price)}</span>
                              </div>
                              <div className="pkgd-price-row">
                                <span>Add‑ons</span>
                                <span>{formatPrice(pricePreview.addons_total)}</span>
                              </div>
                              <div className="pkgd-price-row">
                                <span>Subtotal</span>
                                <span>{formatPrice(pricePreview.subtotal)}</span>
                              </div>
                              <div className="pkgd-price-row">
                                <span>GST ({pricePreview.gst_percentage}%)</span>
                                <span>{formatPrice(pricePreview.gst_amount)}</span>
                              </div>
                              <div className="pkgd-price-row pkgd-total">
                                <span>Grand Total</span>
                                <span>{formatPrice(pricePreview.grand_total)}</span>
                              </div>
                              {formData.couponCode && (
                                <div className="pkgd-price-row pkgd-coupon-note">
                                  <span>Coupon will be applied at booking</span>
                                  <span className="pkgd-coupon-code">{formData.couponCode}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      <div className="pkgd-form-group">
                        <label><FiMessageSquare /> Special Note</label>
                        <textarea
                          name="specialNote"
                          value={formData.specialNote}
                          onChange={handleChange}
                          placeholder="Any special requests or notes..."
                          rows="3"
                        />
                      </div>
                    </div>
                  )}

                  {/* Step 3: Review */}
                  {step === 3 && (
                    <div className="pkgd-form-step pkgd-confirmation-step">
                      <div className="pkgd-confirmation-box">
                        <h4>Review Your Booking</h4>
                        <div className="pkgd-confirmation-details">
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Package</span>
                            <span className="pkgd-conf-value">{packageData?.name}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Destination</span>
                            <span className="pkgd-conf-value">{destinationName}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Duration</span>
                            <span className="pkgd-conf-value">
                              {packageData?.duration_days} Days, {packageData?.duration_nights} Nights
                            </span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Name</span>
                            <span className="pkgd-conf-value">{formData.name}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Email</span>
                            <span className="pkgd-conf-value">{formData.email}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Phone</span>
                            <span className="pkgd-conf-value">{formData.phone}</span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Start Date</span>
                            <span className="pkgd-conf-value">
                              {new Date(formData.date).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              })}
                            </span>
                          </div>
                          <div className="pkgd-conf-item">
                            <span className="pkgd-conf-label">Guests</span>
                            <span className="pkgd-conf-value">{formData.guests}</span>
                          </div>
                          {selectedPlan && (
                            <>
                              <div className="pkgd-conf-item">
                                <span className="pkgd-conf-label">Plan</span>
                                <span className="pkgd-conf-value">{selectedPlan.plan_name}</span>
                              </div>
                              <div className="pkgd-conf-item">
                                <span className="pkgd-conf-label">Food</span>
                                <span className="pkgd-conf-value">
                                  {formData.wantVeg ? 'Veg' : ''}{formData.wantVeg && formData.wantNonVeg ? ' & ' : ''}{formData.wantNonVeg ? 'Non‑Veg' : ''}
                                </span>
                              </div>
                              {addons.length > 0 && (
                                <div className="pkgd-conf-item">
                                  <span className="pkgd-conf-label">Add‑ons</span>
                                  <span className="pkgd-conf-value">
                                    {addons.map(a => {
                                      const addon = availableAddons.find(x => x.id === a.id);
                                      return addon ? `${addon.addon_name}×${a.quantity}` : a.id;
                                    }).join(', ')}
                                  </span>
                                </div>
                              )}
                              {formData.couponCode && (
                                <div className="pkgd-conf-item">
                                  <span className="pkgd-conf-label">Coupon</span>
                                  <span className="pkgd-conf-value">{formData.couponCode}</span>
                                </div>
                              )}
                              {pricePreview && (
                                <div className="pkgd-conf-item pkgd-conf-total">
                                  <span className="pkgd-conf-label">Total (before coupon)</span>
                                  <span className="pkgd-conf-value">{formatPrice(pricePreview.grand_total)}</span>
                                </div>
                              )}
                            </>
                          )}
                          {formData.specialNote && (
                            <div className="pkgd-conf-item">
                              <span className="pkgd-conf-label">Note</span>
                              <span className="pkgd-conf-value">{formData.specialNote}</span>
                            </div>
                          )}
                        </div>
                        <div className="pkgd-confirmation-note">
                          <FiAlertCircle />
                          <span>By confirming, you agree to our terms and conditions.</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pkgd-modal-actions">
                    {step > 1 && (
                      <button type="button" className="pkgd-btn-prev" onClick={handlePrev}>
                        Back
                      </button>
                    )}
                    {step < 3 ? (
                      <button type="button" className="pkgd-btn-next" onClick={handleNext}>
                        Next Step <FiChevronRight />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        className="pkgd-btn-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="pkgd-spinner"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <FiLock />
                            Confirm Booking
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </>
            ) : (
              // ----- Booking Created: Show Summary and Pay Now -----
              <div className="pkgd-booking-summary-full">
                {paymentStep === 'completed' ? (
                  <div className="pkgd-payment-success">
                    <FiCheckCircle className="pkgd-success-icon" />
                    <h3>Payment Successful!</h3>
                    <p>Your honeymoon booking is now confirmed.</p>
                    {bookingResult && (
                      <p className="pkgd-booking-number">
                        Booking #: {bookingResult.booking_code || bookingResult.booking_id}
                      </p>
                    )}
                    <button
                      className="pkgd-btn-close"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                    >
                      Close
                    </button>
                  </div>
                ) : paymentStep === 'failed' ? (
                  <div className="pkgd-payment-failed">
                    <FiAlertCircle className="pkgd-error-icon" />
                    <h3>Payment Failed</h3>
                    <p>{error || 'Something went wrong. Please try again.'}</p>
                    <button
                      className="pkgd-btn-retry"
                      onClick={() => {
                        setPaymentStep('idle');
                        setError(null);
                      }}
                    >
                      Retry Payment
                    </button>
                  </div>
                ) : (
                  <>
                    <h3>Booking Created!</h3>
                    <p className="pkgd-booking-number">
                      Booking #: {bookingResult.booking_code || bookingResult.booking_id}
                    </p>
                    <div className="pkgd-booking-summary">
                      <div className="pkgd-summary-row">
                        <span>Package</span>
                        <span>{bookingResult.package?.name}</span>
                      </div>
                      <div className="pkgd-summary-row">
                        <span>Plan</span>
                        <span>{bookingResult.plan?.plan_name}</span>
                      </div>
                      <div className="pkgd-summary-row">
                        <span>Start Date</span>
                        <span>{new Date(bookingResult.tour_start_date).toLocaleDateString('en-IN')}</span>
                      </div>
                      <div className="pkgd-summary-row">
                        <span>Guests</span>
                        <span>{bookingResult.pax_count}</span>
                      </div>
                      {bookingResult.coupon && (
                        <div className="pkgd-summary-row">
                          <span>Coupon</span>
                          <span>{bookingResult.coupon.code} (discount: {formatPrice(bookingResult.coupon.discount_amount)})</span>
                        </div>
                      )}
                      <div className="pkgd-summary-row">
                        <span>Subtotal</span>
                        <span>{formatPrice(bookingResult.subtotal)}</span>
                      </div>
                      <div className="pkgd-summary-row">
                        <span>GST ({bookingResult.gst_percentage}%)</span>
                        <span>{formatPrice(bookingResult.gst_amount)}</span>
                      </div>
                      <div className="pkgd-summary-row pkgd-total">
                        <span>Grand Total</span>
                        <span>{formatPrice(bookingResult.grand_total)}</span>
                      </div>
                    </div>
                    <p className="pkgd-booking-status">
                      Status: <span className="pkgd-status-pending">{bookingResult.booking_status}</span>
                    </p>
                    <button
                      className="pkgd-btn-pay"
                      onClick={handlePayNow}
                      disabled={paymentStep === 'creating' || paymentStep === 'processing'}
                    >
                      {paymentStep === 'creating' || paymentStep === 'processing' ? (
                        <>
                          <FiLoader className="pkgd-spinner" /> Processing...
                        </>
                      ) : (
                        <>
                          <FiCreditCard /> Pay Now
                        </>
                      )}
                    </button>
                    <button
                      className="pkgd-btn-close"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                    >
                      Close
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}